import { FC, ReactNode, useMemo, useState } from 'react';
import Button, { ButtonVariant } from 'src/components/atoms/Button';
import { trackPendoEvent } from 'src/helpers/trackPendoEvent';
import { IEntry, IFolder, IProof } from 'src/ts/interfaces';
import { IListCheckResolutionRequest } from 'src/ts/interfaces/listCheck';
import { partitionListChecks, trendAnswerProps, trendEdited } from '../../helpers/listCheckHelpers';
import { FormValues } from '../../helpers/proofHelpers';
import { useCandidateXray } from '../../hooks/useCandidateXray';
import { useListCheckResolution } from '../../hooks/useListCheckResolution';
import { useListCheckRulings } from '../../hooks/useListCheckRulings';
import { useProofReviewForm } from '../../hooks/useProofReviewForm';
import { useViewerModeTime } from '../../hooks/useViewerModeTime';
import { useXrayAnalysis } from '../../hooks/useXrayAnalysis';
import {
  ListChecksCenteredState,
  ListChecksLayout,
  ReportPlatformFeedbackLink
} from '../../styles';
import DocumentControls from '../DocumentControls';
import ListCheckDetailPane from '../ListChecksPanel/ListCheckDetailPane';
import ViewerModePill, { ViewerMode } from '../ViewerModePill';
import XrayPanel from '../XrayPanel';
import CheckRail from './CheckRail';
import DocumentSwitcher from './DocumentSwitcher';
import InspectorDocument from './InspectorDocument';
import {
  Body,
  CheckRailRegion,
  DocumentColumn,
  DocumentRegion,
  InspectorListChecks,
  MainColumn,
  Panes,
  RailApplicant,
  RailFooter,
  RailHeader,
  RailHeaderText,
  RailNotice,
  RailProperty,
  RailScroll,
  Shell,
  SidePanelRegion
} from './styles';

interface IProps {
  entry: IEntry;
  folder: IFolder | null;
  currentProof: IProof | undefined;
  proofs: IProof[];
  avRuledProofs?: IProof[];
  onProofChange: (proofId: string) => void;
  isFirstDocument: boolean;
  isLastDocument: boolean;
  canNavigateBack: boolean;
  canNavigateForward: boolean;
  onNextDocument: () => void;
  onPreviousDocument: () => void;
  onReviewReport: () => void;
  onReportPlatformFeedback?: () => void;
  localFormData?: Record<string, FormValues>;
  onUpdateLocalFormData?: (
    updater: (prev: Record<string, FormValues>) => Record<string, FormValues>
  ) => void;
  hideResultControls?: boolean;
  onReclassifyToOfferLetter?: (proofId: string) => void;
  onReclassifyFromOfferLetter?: (proofId: string, newType: IProof['type']) => void;
  onReclassifyCheckSetChange?: (proofId: string, newType: IProof['type']) => Promise<boolean>;
  isPropertyOlvEnabled?: boolean;
  sidePanel?: ReactNode;
}

const InspectorPanel: FC<IProps> = ({
  entry,
  folder,
  currentProof,
  proofs,
  avRuledProofs = [],
  onProofChange,
  isFirstDocument,
  isLastDocument,
  canNavigateBack,
  canNavigateForward,
  onNextDocument,
  onPreviousDocument,
  onReviewReport,
  onReportPlatformFeedback,
  localFormData = {},
  onUpdateLocalFormData,
  hideResultControls,
  onReclassifyToOfferLetter,
  onReclassifyFromOfferLetter,
  onReclassifyCheckSetChange,
  isPropertyOlvEnabled,
  sidePanel
}) => {
  const [viewerMode, setViewerMode] = useState<ViewerMode>('document');
  const [isCheckOpen, setIsCheckOpen] = useState(false);
  const [pick, setPick] = useState<{ proofId: string; checkKey: string } | null>(null);
  const proofIds = useMemo(() => proofs.map((proof) => proof.id), [proofs]);
  const xray = useXrayAnalysis(proofIds, currentProof?.id, true);
  const listChecks = useListCheckRulings(currentProof?.id, true);
  const candidateXray = useCandidateXray(currentProof?.id, true);
  const listCheckResolution = useListCheckResolution();
  const reviewForm = useProofReviewForm(
    currentProof,
    localFormData,
    onUpdateLocalFormData,
    hideResultControls
  );
  const hasListChecks = !!listChecks.data && listChecks.data.status !== null;
  const isXrayTab = viewerMode === 'xray';
  const isListChecksActive = hasListChecks && isCheckOpen;
  const isXrayActive = isXrayTab && !isListChecksActive;
  const activeMode: ViewerMode = isListChecksActive
    ? 'list_checks'
    : isXrayActive
    ? 'xray'
    : 'document';
  useViewerModeTime(activeMode, true, 'inspector');

  const rulings = listChecks.data?.rulings ?? null;
  const isRulingsError = listChecks.status === 'error';
  const isLoadingRulings = listChecks.status === 'loading';
  const pickedKey = pick && pick.proofId === currentProof?.id ? pick.checkKey : null;
  const { needsReview, reviewed, cleared } = partitionListChecks(rulings ?? []);
  const selectedEntry =
    rulings?.find((ruling) => ruling.check_key === pickedKey) ??
    needsReview[0] ??
    reviewed[0] ??
    cleared[0];

  const selectCheck = (checkKey: string) => {
    if (!currentProof) return;
    setPick({ proofId: currentProof.id, checkKey });
    setIsCheckOpen(true);
  };

  const answer = async (checkKey: string, request: IListCheckResolutionRequest | null) => {
    if (!currentProof) return;
    const proofId = currentProof.id;
    setPick({ proofId, checkKey });
    const prior = rulings?.find((ruling) => ruling.check_key === checkKey);
    const resolved = request
      ? await listCheckResolution.resolve(proofId, checkKey, request)
      : await listCheckResolution.undo(proofId, checkKey);
    if (!resolved) return;
    trackPendoEvent('list_check_answered', {
      check_key: checkKey,
      answer: request?.resolution ?? 'undo',
      prior_ruling: prior?.ruling ?? 'unknown',
      prior_source: prior?.source ?? 'none',
      candidate_count: prior?.candidates.length ?? 0,
      surface: 'inspector',
      ...trendAnswerProps(prior, trendEdited(request?.resolution))
    });
    listChecks.applyEntry(proofId, resolved);
  };

  return (
    <Shell data-testid="inspector-panel" data-proof-id={currentProof?.id}>
      <Body>
        <MainColumn>
          <Panes>
            <CheckRailRegion data-testid="inspector-check-rail" aria-label="Checks">
              <RailHeader data-testid="inspector-rail-header">
                <RailHeaderText>
                  <RailApplicant>{entry?.applicant?.full_name}</RailApplicant>
                  <RailProperty>{folder?.property?.name}</RailProperty>
                  {onReportPlatformFeedback && (
                    <ReportPlatformFeedbackLink
                      id="inspector-report-platform-feedback"
                      data-testid="inspector-report-platform-feedback"
                      onClick={onReportPlatformFeedback}
                    >
                      Report a bug
                    </ReportPlatformFeedbackLink>
                  )}
                </RailHeaderText>
                {currentProof && (
                  <ViewerModePill
                    mode={activeMode}
                    showXray
                    showListChecks={false}
                    onChange={(mode) => {
                      setIsCheckOpen(false);
                      setViewerMode(mode);
                    }}
                  />
                )}
              </RailHeader>
              <RailScroll>
                {isRulingsError && (
                  <RailNotice role="alert" data-testid="inspector-list-checks-error">
                    <span>List checks failed to load</span>
                    <Button
                      name="list-checks-retry"
                      label="Retry"
                      variant={ButtonVariant.outline}
                      onClick={listChecks.retry}
                    >
                      Retry
                    </Button>
                  </RailNotice>
                )}
                <CheckRail
                  rulings={rulings}
                  isLoadingRulings={isLoadingRulings}
                  selectedCheckKey={isListChecksActive ? selectedEntry?.check_key : null}
                  onSelectCheck={selectCheck}
                />
              </RailScroll>
              <RailFooter data-testid="inspector-rail-footer">
                <DocumentSwitcher
                  proofs={proofs}
                  avRuledProofs={avRuledProofs}
                  selectedProofId={currentProof?.id}
                  onProofChange={onProofChange}
                  localFormData={localFormData}
                />
                <DocumentControls
                  layout="rail"
                  currentProof={currentProof}
                  selectedProofId={currentProof?.id ?? ''}
                  watchedResult={reviewForm.watchedResult}
                  watchedReason={reviewForm.watchedReason}
                  watchedDocumentType={reviewForm.watchedDocumentType}
                  watchedOlvReasons={reviewForm.watchedOlvReasons}
                  setValue={reviewForm.setValue}
                  isFirstDocument={isFirstDocument}
                  isLastDocument={isLastDocument}
                  canNavigateBack={canNavigateBack}
                  canNavigateForward={canNavigateForward}
                  onNextDocument={onNextDocument}
                  onPreviousDocument={onPreviousDocument}
                  onReviewReport={onReviewReport}
                  proofs={proofs}
                  localFormData={localFormData}
                  hideResultControls={hideResultControls}
                  isOlvActionCommitted={reviewForm.isOlvActionCommitted}
                  onReclassifyToOfferLetter={onReclassifyToOfferLetter}
                  onReclassifyFromOfferLetter={onReclassifyFromOfferLetter}
                  onReclassifyCheckSetChange={onReclassifyCheckSetChange}
                  isPropertyOlvEnabled={isPropertyOlvEnabled}
                />
              </RailFooter>
            </CheckRailRegion>
            <DocumentColumn>
              <DocumentRegion data-testid="inspector-document-region" aria-label="Document">
                <InspectorDocument
                  entry={entry}
                  currentProof={currentProof}
                  showControls={activeMode === 'document'}
                />
                {isXrayActive && (
                  <XrayPanel
                    status={xray.status}
                    data={xray.data}
                    error={xray.error}
                    onRetry={xray.retry}
                  />
                )}
                {isListChecksActive && !selectedEntry && (
                  <ListChecksCenteredState data-testid="list-checks-empty">
                    <span>No checks have answered for this document yet.</span>
                  </ListChecksCenteredState>
                )}
                {isListChecksActive && currentProof && selectedEntry && listChecks.data && (
                  <InspectorListChecks data-testid="inspector-list-check-detail">
                    <ListChecksLayout>
                      <ListCheckDetailPane
                        key={`${currentProof.id}:${selectedEntry.check_key}`}
                        entry={selectedEntry}
                        currentProof={currentProof}
                        surface="inspector"
                        documentXray={xray}
                        candidateXray={candidateXray}
                        isSubmitting={listCheckResolution.isSubmitting}
                        canAnswer={
                          listChecks.data.status !== 'enqueued' &&
                          listChecks.data.status !== 'running'
                        }
                        onSelect={(rowId) =>
                          answer(selectedEntry.check_key, {
                            resolution: 'candidate',
                            row_id: rowId
                          })
                        }
                        onProviderMismatch={(rowId, note) =>
                          answer(selectedEntry.check_key, {
                            resolution: 'provider_mismatch',
                            row_id: rowId,
                            ...(note && { note })
                          })
                        }
                        onNoneOfThese={(note) =>
                          answer(selectedEntry.check_key, {
                            resolution: 'none_of_these',
                            ...(note && { note })
                          })
                        }
                        onFraudTrendAnswer={(edited, note) =>
                          answer(selectedEntry.check_key, {
                            resolution: edited ? 'edited' : 'not_edited',
                            ...(note && { note })
                          })
                        }
                        onUndo={() => answer(selectedEntry.check_key, null)}
                      />
                    </ListChecksLayout>
                  </InspectorListChecks>
                )}
              </DocumentRegion>
            </DocumentColumn>
            {sidePanel && (
              <SidePanelRegion data-testid="inspector-side-panel">{sidePanel}</SidePanelRegion>
            )}
          </Panes>
        </MainColumn>
      </Body>
    </Shell>
  );
};

export default InspectorPanel;
